/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {Logger, PubSub} from '@mamdev/cad.static-next-lib';
import Viewport from './viewport';
import {type EventData, type TealiumViewParams, type TeaserBlockItem, type TeaserInfoItem} from './tracking2/interface';

interface HeimspielMessageData {
  data: {
    businessEventType?: string;
    label?: string;
    type?: string;
    tickerName?: string;
  };
}

class Tracking2 {
  private readonly name = 'tracking2';
  private readonly observer: IntersectionObserver;

  constructor() {
    this.observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;
          const componentPath = target.getAttribute('data-component-path');
          const componentName = target.getAttribute('data-component-name');

          if (componentPath) {
            const eventData: EventData = {
              componentPath,
              businessEventType: 'expose'
            };

            if (componentName) {
              eventData.componentName = componentName;
            }

            const teasersNodeList = target.querySelectorAll('[data-mod-name="newteaser"]');
            const teasersInfo = this.getBlockTeaserInfoTrackingData(teasersNodeList);
            if (teasersInfo) {
              eventData.teasersInfo = teasersInfo;
            }

            this.callTealiumLink(eventData);
          }

          this.observer.unobserve(target);
        }
      });
    }, {threshold: 0.3});

    this.setupExternalMessageTracking();
  }

  setupExternalMessageTracking() {
    window.addEventListener('message', (e: HeimspielMessageData) => {
      if (e.data.businessEventType === 'pageView' && e.data.type === 'ticker') {
        const contentCategoryExtension = `.${e.data.type}.${this.removeAllSpaces(e.data.tickerName)}`;
        const contentNameExtension = `.${this.removeAllSpaces(e.data.label)}`;

        this.callTealiumView({contentCategoryExtension: contentCategoryExtension, contentNameExtension: contentNameExtension});
        PubSub.publish('ad:refreshAll');
      }
    });
  }

  /** New Tealium Virtual-PI tracking. -> https://mam-confluence.1and1.com/x/3Ea9EQ */
  callTealiumView(params: TealiumViewParams = {}) {
    try {
      if (this.isTealiumAvailable()) {
        const utagClone = JSON.parse(JSON.stringify(window.utag_data)) as EventData;
        utagClone.pageViewId = window.crypto.randomUUID();
        utagClone.eventType = 'contentchange';

        if (params.newContentType) {
          utagClone.contentType = params.newContentType;
        }
        if (params.newContentName) {
          utagClone.contentName = params.newContentName;
        }
        if (params.newAgof) {
          utagClone.agof = params.newAgof;
        }
        if (params.newPageType) {
          utagClone.pageType = params.newPageType;
        }
        if (params.contentTypeExtension) {
          utagClone.contentType = utagClone.contentType + params.contentTypeExtension;
        }
        if (params.contentCategoryExtension) {
          utagClone.contentCategory = utagClone.contentCategory + params.contentCategoryExtension;
        }
        if (params.contentNameExtension) {
          utagClone.contentName = utagClone.contentName + params.contentNameExtension;
        }

        window.utag.view(utagClone);
      }
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  /** Inserts required tracking data that cannot be supplied by the backend */
  updateUtagData(adblockDetected: boolean) {
    try {
      if (this.isTealiumAvailable()) {
        window.utag_data.businessEventType = 'update';
        window.utag_data.componentPath = 'page';

        window.utag_data.trackingInitiator = window.utag_data.softwareName;
        window.utag_data.trackingInitiatorVersion = window.utag_data.softwareVersion;

        window.utag_data.layoutClass = Viewport.getLayoutClassForCurrentViewPortWidth();
        window.utag_data.adblockDetected = adblockDetected;

        window.utag.link(window.utag_data);
      }
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  /** New Tealium Event tracking. */
  callTealiumLink(eventData: EventData) {
    try {
      if (this.isTealiumAvailable()) {
        let utagData = JSON.parse(JSON.stringify(window.utag_data));
        utagData = Object.assign(utagData, eventData);
        window.utag.link(utagData);
      } else {
        console.log('callTealiumLink', eventData);
      }
    } catch (e) {
      Logger.logUnknownError(e, this.name);
    }
  }

  private getTeaserTrackingData(elem: HTMLElement) {
    const data: TeaserInfoItem = {};
    // check if elem is a teaser link
    if (elem.parentElement && (elem.parentElement.getAttribute('data-mod-name') === 'newteaser')) {
      const elemWithTeaserInfo = elem.parentElement; // where to read the attributes
      data.teaserBlockName = elemWithTeaserInfo.getAttribute('data-block-name') ?? '';
      const blockItem: TeaserBlockItem = {
        articleId: elemWithTeaserInfo.getAttribute('data-article-id') ?? '',
        teaserItemHeadline: elemWithTeaserInfo.querySelector('.teaser__headline')?.innerHTML ?? '',
        teaserItemPosition: elemWithTeaserInfo.getAttribute('data-item-position') ?? '',
        teaserItemSource: elemWithTeaserInfo.getAttribute('data-item-source') ?? ''
      };
      data.teaserBlockItems = [blockItem];
      return data;
    } else if (elem.hasAttribute('data-block-name')) {
      // e.g. the clickable overview-block title
      data.teaserBlockName = elem.getAttribute('data-block-name') ?? '';
      return data;
    }
    return null;
  }

  bindTrackingClickHandler(elements: Element | NodeList) {
    const trackedElements = elements instanceof NodeList ? Array.from(elements) : [elements];

    // console.log('[tracking2] trackedElements', trackedElements);

    trackedElements.forEach(trackedElement => {
      if (trackedElement instanceof Element
        && trackedElement.hasAttribute('data-click-tracking')) {
        return;
      }

      trackedElement.addEventListener('click', (event: Event) => {
        const target = event.currentTarget as HTMLElement;
        const targetUrl = (target instanceof HTMLAnchorElement && target.href.length > 1) ? target.href : window.ui?.pageinfo?.home;
        const elementTagName = target.tagName.toLowerCase();
        const componentPath = target.getAttribute('data-component-path');
        const componentLabel = this.sanitizeInnerText(target.innerText) || '';

        if (componentPath) {
          const eventData: EventData = {
            componentPath,
            componentLabel,
            targetUrl,
            elementTagName,
            businessEventType: 'userAction',
            eventType: 'mousedown'
          };

          // append teaser click data
          const teaserInfoData = this.getTeaserTrackingData(target);

          if (teaserInfoData) {
            eventData.teasersInfo = teaserInfoData;
          }

          const componentName = target.getAttribute('data-component-name');
          if (componentName) {
            eventData.componentName = componentName;
          }

          this.callTealiumLink(eventData);
        }
      });

      if (trackedElement instanceof HTMLElement) {
        trackedElement.setAttribute('data-click-tracking', 'true');
      }
    });
  }

  bindTrackingExposeHandler(elements?: Element | NodeList) {
    if (elements) {
      const trackedElements = elements instanceof NodeList ? Array.from(elements) : [elements];

      trackedElements.forEach(trackedElement => {
        if (trackedElement instanceof Element
          && trackedElement.hasAttribute('data-expose-tracking')) {
          return;
        }
        if (trackedElement instanceof Element) {
          this.observer.observe(trackedElement);
          trackedElement.setAttribute('data-expose-tracking', 'true');
        }
      });
    }
  }

  getBlockTeaserInfoTrackingData(teasersNodeList: NodeListOf<Element>) {
    const teaserList = Array.from(teasersNodeList);

    if (teaserList.length) {
      const data: TeaserInfoItem = {
        teaserBlockName: '',
        teaserBlockItems: []
      };

      data.teaserBlockName = teaserList[0].getAttribute('data-block-name') ?? '';

      teaserList.forEach(teaser => {
        const blockItem: TeaserBlockItem = {
          articleId: teaser.getAttribute('data-article-id') ?? '',
          teaserItemHeadline: teaser.querySelector('.teaser__headline')?.innerHTML ?? '',
          teaserItemPosition: teaser.getAttribute('data-item-position') ?? '',
          teaserItemSource: teaser.getAttribute('data-item-source') ?? ''
        };
        data.teaserBlockItems?.push(blockItem);
      });

      return data;
    }

    return null;
  }

  private sanitizeInnerText(text: string) {
    return text.split('\n').join(' ');
  }

  private removeAllSpaces(input?: string): string {
    if (input) {
      return input.replaceAll(' ', '');
    }
    return '';
  }

  /** Check if Tealium and utag_data is available on the page */
  isTealiumAvailable(): boolean {
    return ((typeof window.utag !== 'undefined') && (typeof window.utag_data !== 'undefined'));
  }
}

export default new Tracking2();
